import React from "react";
import {
  getCollectionSettings,
  saveUserSettings,
} from "../api/collectionSettings";

export default function useCollectionSettings({
  username,
  profileVersion,
  refreshing = false,
}) {
  const [state, setState] = React.useState({});
  const owner = React.useRef(username);
  const generation = React.useRef(0);
  const reader = React.useRef(null);
  const saving = React.useRef(false);
  const refreshNeeded = React.useRef(false);
  const mounted = React.useRef(false);
  owner.current = username;

  const reload = React.useCallback(async () => {
    if (!username) return;
    if (saving.current) {
      refreshNeeded.current = true;
      return;
    }
    reader.current?.abort();
    const controller = new AbortController();
    reader.current = controller;
    const revision = ++generation.current;
    setState((previous) => ({
      ...(previous.username === username ? previous : {}),
      username,
      loading: true,
    }));
    try {
      const data = await getCollectionSettings(controller.signal);
      if (
        mounted.current &&
        !controller.signal.aborted &&
        revision === generation.current &&
        owner.current === username
      ) {
        setState({ username, data });
      }
    } catch (error) {
      if (
        mounted.current &&
        !controller.signal.aborted &&
        revision === generation.current &&
        owner.current === username
      ) {
        setState((previous) => ({
          ...(previous.username === username ? previous : {}),
          username,
          loading: false,
          error,
        }));
      }
    }
  }, [username]);

  React.useEffect(() => {
    mounted.current = true;
    setState({});
    saving.current = false;
    refreshNeeded.current = false;
    return () => {
      mounted.current = false;
      generation.current += 1;
      reader.current?.abort();
    };
  }, [username]);
  // useUserProfile refreshes profileVersion on focus, pageshow and account changes.
  React.useEffect(() => {
    reload();
  }, [reload, profileVersion]);

  const save = async (value, field = "sync_collection_with_discogs") => {
    if (
      !username ||
      owner.current !== username ||
      !mounted.current ||
      refreshing ||
      saving.current
    )
      return false;
    reader.current?.abort();
    const revision = ++generation.current;
    saving.current = true;
    setState((previous) => ({
      ...previous,
      username,
      saving: true,
      saveError: null,
      saveField: field,
    }));
    try {
      const data = await saveUserSettings({ [field]: value });
      if (
        !mounted.current ||
        owner.current !== username ||
        generation.current !== revision
      )
        return false;
      setState({ username, data });
      return true;
    } catch (saveError) {
      if (
        mounted.current &&
        owner.current === username &&
        generation.current === revision
      ) {
        setState((previous) => ({ ...previous, saving: false, saveError }));
      }
      return false;
    } finally {
      if (owner.current === username && generation.current === revision) {
        saving.current = false;
        if (refreshNeeded.current) {
          refreshNeeded.current = false;
          reload();
        }
      }
    }
  };
  const current = state.username === username ? state : {};
  const collectionSaveError =
    current.saveField === "dark_mode" ? null : current.saveError;
  return {
    darkMode: current.data?.dark_mode,
    appearanceError:
      current.saveField === "dark_mode" ? current.saveError : null,
    appearanceSaving: !!current.saving && current.saveField === "dark_mode",
    canSaveAppearance:
      !!current.data &&
      !current.loading &&
      !current.error &&
      !current.saving &&
      !refreshing,
    saveDarkMode: (value) => save(value, "dark_mode"),
    syncPreference: current.data?.sync_collection_with_discogs,
    ready:
      !!username &&
      !!current.data &&
      !current.loading &&
      !current.saving &&
      !current.error &&
      !collectionSaveError &&
      !refreshing,
    loading:
      !!username &&
      (!state.username ||
        state.username !== username ||
        current.loading ||
        refreshing),
    saving: !!current.saving,
    error: current.error,
    saveError: collectionSaveError,
    reload,
    save,
  };
}
